import React, { useEffect, useMemo, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import DataPageHeader from '../components/Common/DataPageHeader';
import { msalConfig, loginRequest, getApiUrl, getRawDateString } from '../authConfig';

const assignedToOptions = [
  'info@orientalbiz.ca',
  'volunteer@orientalbiz.ca',
  'cvitp@orientalbiz.ca',
  'admin@orientalbiz.ca',
];

const statusOptions = ['Open', 'Pending', 'Draft Sent', 'Payment Pending', 'Completed'];

const getStatusBadgeClass = (status) => {
  const map = {
    Open: 'bg-secondary',
    Pending: 'bg-warning text-dark',
    'Draft Sent': 'bg-info text-dark',
    'Payment Pending': 'bg-danger',
    Completed: 'bg-success',
  };
  return `badge badge-status ${map[status] || 'bg-secondary'}`;
};

const initialCorporateCustomer = {
  businessName: '',
  contactName: '',
  businessNumber: '',
  address: '',
  mailId: '',
  yearEnd: '',
  toDoList: '',
  details: '',
  mobile: '',
  DateFiled: '',
  corporateIncomeTax: '',
  status: 'Pending',
  invoiceAmount: '',
  invoiceStatus: '',
  paymentStatus: '',
  notes: '',
  assignedTo: '',
  dueDate: '',
};

const getId = (record) => record._id || record.id;

const getDueDateStyle = (dueDate) => {
  if (!dueDate) return {};
  try {
    const dueDateObj = new Date(dueDate);
    const today = new Date();
    const dueDateStr = dueDateObj.toISOString().split('T')[0];
    const todayStr = today.toISOString().split('T')[0];

    if (dueDateStr === todayStr) {
      return { backgroundColor: '#fff3cd', fontWeight: 'bold' };
    }
    if (dueDateStr < todayStr) {
      return { backgroundColor: '#f8d7da', fontWeight: 'bold' };
    }
  } catch {
    return {};
  }
  return {};
};

const compareDueDates = (a, b) => {
  const aDate = a.dueDate;
  const bDate = b.dueDate;

  if (!aDate && !bDate) return 0;
  if (!aDate) return 1;
  if (!bDate) return -1;

  const parsedA = new Date(aDate);
  const parsedB = new Date(bDate);
  if (isNaN(parsedA.getTime()) || isNaN(parsedB.getTime())) return 0;

  return parsedA.getTime() - parsedB.getTime();
};

const CorporateTaxDataPage = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [account, setAccount] = useState(null);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState('assignedTo');
  const [sortAsc, setSortAsc] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [newRecord, setNewRecord] = useState(initialCorporateCustomer);
  const [editValues, setEditValues] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);

  const msalInstance = useMemo(() => new PublicClientApplication(msalConfig), []);

  useEffect(() => {
    let mounted = true;

    const initializeMsal = async () => {
      try {
        if (typeof msalInstance.initialize === 'function') {
          await msalInstance.initialize();
        }

        const response = await msalInstance.handleRedirectPromise();
        if (!mounted) return;

        if (response && response.account) {
          setAccount(response.account);
        } else {
          const currentAccounts = msalInstance.getAllAccounts();
          if (currentAccounts.length > 0) {
            setAccount(currentAccounts[0]);
          }
        }
      } catch (err) {
        console.error('MSAL Initialization Error:', err);
        if (mounted) setError('Authentication initialization failed.');
      } finally {
        if (mounted) setIsInitialized(true);
      }
    };

    initializeMsal();
    return () => {
      mounted = false;
    };
  }, [msalInstance]);

  useEffect(() => {
    if (account) {
      fetchRecords();
    }
  }, [account]);

  const fetchRecords = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const response = await fetch(getApiUrl('/api/corporate'), {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load corporate customer data.');
      }
      const data = await response.json();
      setRecords(data || []);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Could not verify your session. Please sign in again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!isInitialized) {
      setError('Authentication is still initializing. Please wait.');
      return;
    }
    setError('');
    setMessage('');

    if (!process.env.REACT_APP_MSAL_CLIENT_ID || process.env.REACT_APP_MSAL_CLIENT_ID === 'YOUR_CLIENT_ID_HERE') {
      setError('Please configure REACT_APP_MSAL_CLIENT_ID in your .env file.');
      return;
    }

    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (loginError) {
      console.error(loginError);
      setError(loginError.message || 'Microsoft sign-in failed.');
    }
  };

  const handleLogout = async () => {
    try {
      await msalInstance.logoutRedirect({ account });
    } catch (logoutError) {
      console.error(logoutError);
      setError(logoutError.message || 'Logout failed.');
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const handleAddRecord = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!newRecord.businessName || !newRecord.mobile) {
      setError('Business name and mobile number are required.');
      return;
    }

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 3);
    const dueDateString = dueDate.toISOString().split('T')[0];

    const payload = {
      ...newRecord,
      dueDate: dueDateString,
      updatedBy: account?.username || account?.name || 'Unknown',
    };

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const response = await fetch(getApiUrl('/api/corporate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.message || 'Unable to add corporate record.');
      }

      const created = await response.json();
      setRecords((prev) => [created, ...prev]);
      setNewRecord(initialCorporateCustomer);
      setShowAddModal(false);
      setMessage('Corporate record added successfully.');
    } catch (saveError) {
      console.error(saveError);
      setError('Could not verify your session. Please sign in again.');
    }
  };

  const handleUpdateRecord = async (record) => {
    const id = getId(record);
    const currentEdit = editValues[id] || {};
    const updates = {
      businessName: currentEdit.businessName ?? record.businessName,
      contactName: currentEdit.contactName ?? record.contactName,
      businessNumber: currentEdit.businessNumber ?? record.businessNumber,
      address: currentEdit.address ?? record.address,
      mailId: currentEdit.mailId ?? record.mailId,
      yearEnd: currentEdit.yearEnd ?? record.yearEnd,
      toDoList: currentEdit.toDoList ?? record.toDoList,
      details: currentEdit.details ?? record.details,
      mobile: currentEdit.mobile ?? record.mobile,
      DateFiled: currentEdit.DateFiled ?? record.DateFiled,
      corporateIncomeTax: currentEdit.corporateIncomeTax ?? record.corporateIncomeTax,
      status: currentEdit.status ?? record.status,
      invoiceAmount: currentEdit.invoiceAmount ?? record.invoiceAmount,
      invoiceStatus: currentEdit.invoiceStatus ?? record.invoiceStatus,
      paymentStatus: currentEdit.paymentStatus ?? record.paymentStatus,
      notes: currentEdit.notes ?? record.notes,
      assignedTo: currentEdit.assignedTo ?? record.assignedTo,
      dueDate: currentEdit.dueDate ?? record.dueDate,
      updatedBy: account?.username || account?.name || 'Unknown',
    };

    setSavingId(id);
    setError('');
    setMessage('');

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const response = await fetch(getApiUrl(`/api/corporate/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.message || 'Unable to update corporate record.');
      }

      const updated = await response.json();
      setRecords((prev) => prev.map((item) => (getId(item) === id ? updated : item)));
      setMessage('Corporate record updated.');
    } catch (updateError) {
      console.error(updateError);
      setError('Could not verify your session. Please sign in again.');
    } finally {
      setSavingId(null);
    }
  };

  const handleAssignedToChange = async (record, assignedTo) => {
    const id = getId(record);
    setSavingId(id);
    setError('');
    setMessage('');

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const response = await fetch(getApiUrl(`/api/corporate/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
        body: JSON.stringify({
          assignedTo,
          updatedBy: account?.username || account?.name || 'Unknown',
        }),
      });

      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.message || 'Unable to update assignment.');
      }

      const updated = await response.json();
      setRecords((prev) => prev.map((item) => (getId(item) === id ? updated : item)));
      setMessage('Assignment updated.');
    } catch (updateError) {
      console.error(updateError);
      setError('Could not verify your session. Please sign in again.');
    } finally {
      setSavingId(null);
    }
  };

  const handleRowExpand = (recordId, record) => {
    setExpandedId(expandedId === recordId ? null : recordId);
    setEditValues((prev) => ({
      ...prev,
      [recordId]: {
        businessName: record.businessName || '',
        contactName: record.contactName || '',
        businessNumber: record.businessNumber || '',
        address: record.address || '',
        mailId: record.mailId || '',
        yearEnd: record.yearEnd || '',
        toDoList: record.toDoList || '',
        details: record.details || '',
        mobile: record.mobile || '',
        DateFiled: record.DateFiled || '',
        corporateIncomeTax: record.corporateIncomeTax || '',
        status: record.status || '',
        invoiceAmount: record.invoiceAmount || '',
        invoiceStatus: record.invoiceStatus || '',
        paymentStatus: record.paymentStatus || '',
        notes: record.notes || '',
        assignedTo: record.assignedTo || '',
        dueDate: record.dueDate || '',
      },
    }));
  };

  const renderCorporateExpandedForm = (record, recordId) => (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Edit Record Details</h6>
        <button
          className="btn btn-success btn-sm"
          onClick={() => handleUpdateRecord(record)}
          disabled={savingId === recordId}
        >
          {savingId === recordId ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      <div className="row g-3">
        <div className="col-md-4">
          <label className="form-label">Status</label>
          <input
            className="form-control"
            value={editValues[recordId]?.status || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  status: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Assigned To</label>
          <select
            className="form-select"
            value={editValues[recordId]?.assignedTo || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  assignedTo: e.target.value,
                },
              }))
            }
          >
            <option value="">Unassigned</option>
            {assignedToOptions.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">Due Date</label>
          <input
            type="date"
            className="form-control"
            value={getRawDateString(editValues[recordId]?.dueDate)}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  dueDate: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-12">
          <label className="form-label">To Do List</label>
          <textarea
            className="form-control"
            rows="2"
            value={editValues[recordId]?.toDoList || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  toDoList: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Business Name</label>
          <input
            className="form-control"
            value={editValues[recordId]?.businessName || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  businessName: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Contact Name</label>
          <input
            className="form-control"
            value={editValues[recordId]?.contactName || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  contactName: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Business Number</label>
          <input
            className="form-control"
            value={editValues[recordId]?.businessNumber || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  businessNumber: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Email / Mail ID</label>
          <input
            className="form-control"
            value={editValues[recordId]?.mailId || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  mailId: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Year End</label>
          <input
            className="form-control"
            value={editValues[recordId]?.yearEnd || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  yearEnd: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Date Filed</label>
          <input
            type="date"
            className="form-control"
            value={getRawDateString(editValues[recordId]?.DateFiled)}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  DateFiled: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Corporate Income Tax</label>
          <input
            className="form-control"
            value={editValues[recordId]?.corporateIncomeTax || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  corporateIncomeTax: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Invoice Amount</label>
          <input
            className="form-control"
            value={editValues[recordId]?.invoiceAmount || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  invoiceAmount: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Invoice Status</label>
          <input
            className="form-control"
            value={editValues[recordId]?.invoiceStatus || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  invoiceStatus: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Payment Status</label>
          <input
            className="form-control"
            value={editValues[recordId]?.paymentStatus || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  paymentStatus: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-12">
          <label className="form-label">Address</label>
          <input
            className="form-control"
            value={editValues[recordId]?.address || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  address: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-12">
          <label className="form-label">Details</label>
          <textarea
            className="form-control"
            rows="2"
            value={editValues[recordId]?.details || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  details: e.target.value,
                },
              }))
            }
          />
        </div>
        <div className="col-md-12">
          <label className="form-label">Notes</label>
          <textarea
            className="form-control"
            rows="2"
            value={editValues[recordId]?.notes || ''}
            onChange={(e) =>
              setEditValues((prev) => ({
                ...prev,
                [recordId]: {
                  ...prev[recordId],
                  notes: e.target.value,
                },
              }))
            }
          />
        </div>
      </div>
      <div className="text-end mt-3">
        <button
          className="btn btn-success"
          onClick={() => handleUpdateRecord(record)}
          disabled={savingId === recordId}
        >
          {savingId === recordId ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );

  const handleOpenAddModal = () => {
    setError('');
    setMessage('');
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewRecord(initialCorporateCustomer);
  };

  const filteredData = records.filter((record) => {
    const assignedMatch = filterAssignedTo ? record.assignedTo === filterAssignedTo : true;
    const rawValue = `${record.businessName} ${record.contactName} ${record.businessNumber} ${record.mobile} ${record.status} ${record.assignedTo} ${record.details} ${record.notes}`.toLowerCase();
    return assignedMatch && rawValue.includes(searchText.toLowerCase());
  });

  const sortedData = [...filteredData].sort((a, b) => {
    const currentUser = account?.username?.toLowerCase();
    const aAssignedMatch = currentUser && a.assignedTo?.toLowerCase() === currentUser ? 0 : 1;
    const bAssignedMatch = currentUser && b.assignedTo?.toLowerCase() === currentUser ? 0 : 1;
    if (aAssignedMatch !== bAssignedMatch) {
      return aAssignedMatch - bAssignedMatch;
    }

    if (sortKey === 'dueDate') {
      const comparison = compareDueDates(a, b);
      return sortAsc ? comparison : -comparison;
    }

    const aVal = typeof a[sortKey] === 'string' ? a[sortKey].toLowerCase() : a[sortKey] || '';
    const bVal = typeof b[sortKey] === 'string' ? b[sortKey].toLowerCase() : b[sortKey] || '';
    if (aVal < bVal) return sortAsc ? -1 : 1;
    if (aVal > bVal) return sortAsc ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedData.length / 25));
  const pageData = sortedData.slice(0, 25);

  useEffect(() => {
    setExpandedId(null);
  }, [filterAssignedTo, searchText, records.length]);

  return (
    <div className="container py-5" style={{ minHeight: '100vh' }}>
      <DataPageHeader
        title="Corporate Tax"
        description="Use your Microsoft organization account to access corporate tax records."
        account={account}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />

      {(error || message) && (
        <div className="mb-3">
          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}
        </div>
      )}

      {account ? (
        <>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card data-summary-card h-100 p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <small className="text-muted">Corporate records</small>
                    <h4 className="mb-0">{records.length}</h4>
                  </div>
                  <span className="badge bg-primary">Live</span>
                </div>
                <p className="mb-0 text-muted">Current corporate customers available for review.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div 
                className="card data-summary-card h-100 p-3" 
                style={{ cursor: 'pointer' }}
                onClick={() => setFilterAssignedTo(account?.username || '')}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <small className="text-muted">Assigned to me</small>
                    <h4 className="mb-0">{records.filter((item) => item.assignedTo === account?.username).length}</h4>
                  </div>
                  <span className="badge bg-info text-dark">My queue</span>
                </div>
                <p className="mb-0 text-muted">Records matched to your signed-in account.</p>
              </div>
            </div>
            <div className="col-md-4 d-flex align-items-end justify-content-end">
              <button className="btn btn-primary btn-lg px-4" onClick={handleOpenAddModal}>
                Add Corporate Record
              </button>
            </div>
          </div>

          <div className="card data-summary-card mb-4 p-3">
            <div className="row g-3 align-items-center">
              <div className="col-md-4">
                <div className="input-group shadow-sm rounded-pill overflow-hidden">
                  <span className="input-group-text bg-white border-end-0">🔎</span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search business, contact, mobile, status"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="input-group shadow-sm rounded-pill overflow-hidden">
                  <span className="input-group-text bg-white border-end-0">👤</span>
                  <select
                    className="form-select border-start-0"
                    value={filterAssignedTo}
                    onChange={(e) => setFilterAssignedTo(e.target.value)}
                  >
                    <option value="">All assignees</option>
                    {assignedToOptions.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4 text-md-end">
                <button className="btn btn-outline-primary" onClick={fetchRecords} disabled={loading}>
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>

          <div className="d-block d-md-none">
            {pageData.length === 0 ? (
              <div className="card mb-3 mobile-record-card">
                <div className="card-body text-center py-4">
                  <p className="mb-0 text-muted">No corporate records found.</p>
                </div>
              </div>
            ) : (
              pageData.map((record) => {
                const recordId = getId(record);
                return (
                  <div key={recordId} className="card mb-3 mobile-record-card shadow-sm">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h5 className="mb-1">{record.businessName}</h5>
                          <div className="small text-muted">{record.mobile || 'No mobile'}</div>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          type="button"
                          onClick={() => handleRowExpand(recordId, record)}
                        >
                          {expandedId === recordId ? 'Hide' : 'Details'}
                        </button>
                      </div>
                      <div className="mb-3">
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <span className={getStatusBadgeClass(record.status)}>{record.status || 'Unknown'}</span>
                        </div>
                        <div>
                          <select
                            className="form-select form-select-sm"
                            value={record.assignedTo || ''}
                            disabled={savingId === recordId}
                            onChange={(e) => handleAssignedToChange(record, e.target.value)}
                          >
                            <option value="">Unassigned</option>
                            {assignedToOptions.map((email) => (
                              <option key={email} value={email}>
                                {email}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {expandedId === recordId && (
                        <div className="bg-light rounded-3 p-3">
                          {renderCorporateExpandedForm(record, recordId)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="table-responsive d-none d-md-block">
            <table className="table table-modern table-hover align-middle shadow-sm overflow-hidden">
              <thead>
                <tr className="text-muted small text-uppercase">
                  <th onClick={() => handleSort('businessName')} style={{ cursor: 'pointer' }}>
                    Business Name
                  </th>
                  <th className="d-none d-md-table-cell" onClick={() => handleSort('businessNumber')} style={{ cursor: 'pointer' }}>
                    Business Number
                  </th>
                  <th className="d-none d-md-table-cell" onClick={() => handleSort('mobile')} style={{ cursor: 'pointer' }}>
                    Mobile
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status
                  </th>
                  <th onClick={() => handleSort('assignedTo')} style={{ cursor: 'pointer' }}>
                    Assigned To
                  </th>
                  <th className="d-none d-md-table-cell" onClick={() => handleSort('dueDate')} style={{ cursor: 'pointer' }}>
                    Due Date
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      Loading corporate customer data...
                    </td>
                  </tr>
                ) : pageData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      No corporate records found.
                    </td>
                  </tr>
                ) : (
                  pageData.map((record) => {
                    const recordId = getId(record);
                    return (
                      <React.Fragment key={recordId}>
                        <tr>
                          <td>{record.businessName}</td>
                          <td className="d-none d-md-table-cell">{record.businessNumber}</td>
                          <td className="d-none d-md-table-cell">{record.mobile}</td>
                          <td><span className={getStatusBadgeClass(record.status)}>{record.status || 'Unknown'}</span></td>
                          <td>
                            <select
                              className="form-select"
                              value={record.assignedTo || ''}
                              disabled={savingId === recordId}
                              onChange={(e) => handleAssignedToChange(record, e.target.value)}
                            >
                              <option value="">Unassigned</option>
                              {assignedToOptions.map((email) => (
                                <option key={email} value={email}>
                                  {email}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="d-none d-md-table-cell" style={getDueDateStyle(record.dueDate)}>
                            {getRawDateString(record.dueDate)}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              type="button"
                              onClick={() => handleRowExpand(recordId, record)}
                            >
                              {expandedId === recordId ? 'Hide' : 'Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedId === recordId && (
                          <tr className="table-secondary">
                            <td colSpan="7">
                              {renderCorporateExpandedForm(record, recordId)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {showAddModal && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1050 }}
              onClick={handleCloseAddModal}
            >
              <div
                className="bg-white rounded shadow-lg p-4"
                style={{ width: '95%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">Add Corporate Customer</h4>
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleCloseAddModal}>
                    Close
                  </button>
                </div>
                <form onSubmit={handleAddRecord}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Business Name</label>
                      <input
                        className="form-control"
                        value={newRecord.businessName}
                        onChange={(e) => setNewRecord({ ...newRecord, businessName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Contact Name</label>
                      <input
                        className="form-control"
                        value={newRecord.contactName}
                        onChange={(e) => setNewRecord({ ...newRecord, contactName: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Business Number</label>
                      <input
                        className="form-control"
                        value={newRecord.businessNumber}
                        onChange={(e) => setNewRecord({ ...newRecord, businessNumber: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Mobile</label>
                      <input
                        className="form-control"
                        value={newRecord.mobile}
                        onChange={(e) => setNewRecord({ ...newRecord, mobile: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Mail ID</label>
                      <input
                        className="form-control"
                        value={newRecord.mailId}
                        onChange={(e) => setNewRecord({ ...newRecord, mailId: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Year End</label>
                      <input
                        className="form-control"
                        value={newRecord.yearEnd}
                        onChange={(e) => setNewRecord({ ...newRecord, yearEnd: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Date Filed</label>
                      <input
                        type="date"
                        className="form-control"
                        value={newRecord.DateFiled}
                        onChange={(e) => setNewRecord({ ...newRecord, DateFiled: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Corporate Income Tax</label>
                      <input
                        className="form-control"
                        value={newRecord.corporateIncomeTax}
                        onChange={(e) => setNewRecord({ ...newRecord, corporateIncomeTax: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Invoice Amount</label>
                      <input
                        className="form-control"
                        value={newRecord.invoiceAmount}
                        onChange={(e) => setNewRecord({ ...newRecord, invoiceAmount: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Invoice Status</label>
                      <input
                        className="form-control"
                        value={newRecord.invoiceStatus}
                        onChange={(e) => setNewRecord({ ...newRecord, invoiceStatus: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Payment Status</label>
                      <input
                        className="form-control"
                        value={newRecord.paymentStatus}
                        onChange={(e) => setNewRecord({ ...newRecord, paymentStatus: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Assigned To</label>
                      <select
                        className="form-select"
                        value={newRecord.assignedTo}
                        onChange={(e) => setNewRecord({ ...newRecord, assignedTo: e.target.value })}
                      >
                        <option value="">Unassigned</option>
                        {assignedToOptions.map((email) => (
                          <option key={email} value={email}>
                            {email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Address</label>
                      <input
                        className="form-control"
                        value={newRecord.address}
                        onChange={(e) => setNewRecord({ ...newRecord, address: e.target.value })}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Details</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={newRecord.details}
                        onChange={(e) => setNewRecord({ ...newRecord, details: e.target.value })}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">To Do List</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={newRecord.toDoList}
                        onChange={(e) => setNewRecord({ ...newRecord, toDoList: e.target.value })}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={newRecord.notes}
                        onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button className="btn btn-secondary" type="button" onClick={handleCloseAddModal}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" type="submit">
                      Save Corporate Record
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="alert alert-info">Please sign in with your Microsoft organization account to view the corporate customer portal.</div>
      )}
    </div>
  );
};

export default CorporateTaxDataPage;
